import { ApiClient, type FileResponse } from "@/api/zoolander";
import download from "downloadjs";
import dayjs from "dayjs";
import { useI18n } from "vue-i18n";
import axios from "axios";
import { reactive } from "vue";
import { useNotification } from "./useNotification";

export type BlobType = string | File | Blob | Uint8Array;

export type DownloadQRCodesRequest = {
  format: string;
  spaceResourceIds?: string[] | undefined;
  locationId?: string | undefined;
  locationGroupId?: string | undefined;
  levelId?: string | undefined;
  spaceCategoryId?: string | undefined;
};

export type PreregistrationRequest = {
  visitorCategoryId?: string | undefined;
  locationId?: string | undefined;
};

export const useCsvTemplates = () => {
  const apiClient = new ApiClient();

  const getTemplate = async (
    fileName: string,
    apiResponse: Promise<FileResponse>
  ) => {
    const { data } = await apiResponse;
    download(data, fileName, data.type);
  };

  const downloadResourcesTemplate = (fileName: string) => {
    getTemplate(fileName, apiClient.getSpaceResourceCSVTemplate());
  };

  const downloadVisitorsTemplate = (
    fileName: string,
    request: PreregistrationRequest
  ) => {
    getTemplate(
      fileName,
      apiClient.getPreregistrationCSVTemplate({
        visitorCategoryId: request.visitorCategoryId ?? "",
        locationId: request.locationId ?? "",
      })
    );
  };

  return { downloadResourcesTemplate, downloadVisitorsTemplate };
};

export const useDownloadUtils = () => {
  const { t } = useI18n();
  const notifications = useNotification();
  const isDownloading = reactive<Record<string, boolean>>({});

  const fileTypes = {
    csv: "application/csv",
    pdf: "application/pdf",
  };

  const downloadFile = (
    blob: BlobType,
    startOfName: string,
    mimeType: keyof typeof fileTypes
  ) => {
    const fileName = t(`${startOfName}`, {
      datetime: dayjs().format("YYYY-MM-DD HH.mm.ss"),
    });

    download(blob, `${fileName}.${mimeType}`, fileTypes[mimeType]);
  };

  const downloadQRCode = async (spaceResourceId: string) => {
    const apiClient = new ApiClient();
    const { data, fileName } = await apiClient.getSpaceResourceQRCode({
      id: spaceResourceId,
    });

    download(data, fileName);
  };

  const downloadQRCodes = async (request: DownloadQRCodesRequest) => {
    const apiClient = new ApiClient();
    const { data, fileName } =
      await apiClient.listSpaceResourceQRCodes(request);

    download(data, fileName);
  };

  const downloadDocument = async (
    name: string,
    url: string,
    key: string,
    fullName?: string
  ) => {
    isDownloading[key] = true;
    try {
      const { data } = await axios.get(url, {
        responseType: "blob",
      });
      const filename = name?.split("?")[0].split("/").pop();
      const prettyFileName = t("visitors.fields.documentUploadFileName", {
        name: fullName ?? filename,
        datetime: dayjs().format("YYYY-MM-DD HH.mm.ss"),
      });
      const fileType = filename?.split(".").pop();
      download(data, `${prettyFileName}.${fileType}`, fileType);
    } catch (e) {
      notifications.sendErrorNotification(t("errors.unexpected"));
    } finally {
      isDownloading[key] = false;
    }
  };

  return {
    downloadFile,
    downloadQRCode,
    downloadQRCodes,
    downloadDocument,
    isDownloading,
  };
};
